import React from "react";
import { findPageSizePreset, pageSizeToPx } from "../constants/pageDimensions";
import {
  findBoardLayout, findMatte, layoutBoard, matteWindow, safeAreaRects, normalizeStoryboard,
  MATTE_BAR_FILL, MATTE_GUIDE_SHADE, SAFE_AREA_LINE,
} from "../constants/storyboard";
import { notesLayout, NOTES_RULE, NOTES_HEADER_HEIGHT } from "../utils/shotNotes";

/**
 * A blank board drawn as SVG from the same geometry the editor uses, for
 * template pages. Lightweight, crisp at any size, and safe to pre-render.
 */
const BoardPreview = ({ seed, title, style }) => {
  const { width, height } = pageSizeToPx(findPageSizePreset(seed.pageSizeKey));
  const storyboard = normalizeStoryboard(seed.storyboard);
  const compact = Boolean(findBoardLayout(storyboard.layoutKey).compact);
  const slots = layoutBoard({ pageWidth: width, pageHeight: height, storyboard });
  const matte = findMatte(storyboard.matteKey);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={title}
      style={{ display: "block", width: "100%", height: "auto", background: "#ffffff", ...style }}
    >
      <rect x={0.5} y={0.5} width={width - 1} height={height - 1} fill="#ffffff" stroke="#d8d4cc" />
      {slots.map(({ frame, notes }, index) => {
        const win = matte.ratio ? matteWindow(frame.width, frame.height, matte.ratio) : null;
        const safe = storyboard.safeAreas ? safeAreaRects(win?.width ?? frame.width, win?.height ?? frame.height) : [];
        const { sections } = notesLayout(notes, { compact });
        return (
          <g key={index}>
            <g transform={`translate(${frame.x} ${frame.y})`}>
              {win && (
                <path
                  d={`M0 0H${frame.width}V${frame.height}H0Z M${win.x} ${win.y}h${win.width}v${win.height}h${-win.width}Z`}
                  fillRule="evenodd"
                  fill={matte.mode === "bars" ? MATTE_BAR_FILL : MATTE_GUIDE_SHADE}
                />
              )}
              {safe.map((r) => (
                <rect key={r.key} x={(win?.x ?? 0) + r.x} y={(win?.y ?? 0) + r.y} width={r.width} height={r.height}
                  fill="none" stroke={SAFE_AREA_LINE} strokeWidth={1} strokeDasharray={r.key === "title" ? "3 3" : "8 4"} />
              ))}
              <rect x={1} y={1} width={frame.width - 2} height={frame.height - 2} fill="none" stroke="#1a1a1a" strokeWidth={2} />
            </g>
            <g transform={`translate(${notes.x} ${notes.y})`} fontFamily="Helvetica, Arial, sans-serif">
              <rect x={0.5} y={0.5} width={notes.width - 1} height={notes.height - 1} fill="none" stroke={NOTES_RULE} />
              <line x1={0} x2={notes.width} y1={NOTES_HEADER_HEIGHT} y2={NOTES_HEADER_HEIGHT} stroke={NOTES_RULE} />
              <text x={5} y={NOTES_HEADER_HEIGHT / 2 + 3.5} fontSize={9.5} fontWeight={700} fill="#1a1a1a">
                SHOT {index + 1}
              </text>
              {sections.map((section) => section.showLabel && (
                <text key={section.field} x={section.x} y={section.y + 9} fontSize={7.5} letterSpacing={0.8} fill="#6b665d">
                  {section.label.toUpperCase()}
                </text>
              ))}
            </g>
          </g>
        );
      })}
    </svg>
  );
};

export default BoardPreview;
